import React, { useState, useEffect } from 'react'
import { getAllJobs, addJob, updateJob, deleteJob } from '../../utils/jobStorage'
import JobForm from './JobForm'

const JobManager = ({ onJobChange }) => {
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingJob, setEditingJob] = useState(null)
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')

  useEffect(() => {
    fetchJobs()
  }, [])

  const fetchJobs = async () => {
    try {
      const data = getAllJobs()
      setJobs(data)
    } catch {
      setJobs([])
    } finally {
      setLoading(false)
    }
  }

  const handleAddJob = () => {
    setEditingJob(null)
    setShowForm(true)
  }

  const handleEditJob = (job) => {
    setEditingJob(job)
    setShowForm(true)
  }

  const handleDeleteJob = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      try {
        const success = deleteJob(jobId)
        if (success) {
          setJobs(jobs.filter(job => job.id !== jobId))
          onJobChange()
        }
      } catch {
        alert('Error deleting job. Please try again.')
      }
    }
  }

  const handleToggleActive = async (job) => {
    try {
      const updatedJob = updateJob(job.id, { ...job, active: !job.active })
      if (updatedJob) {
        setJobs(jobs.map(j => 
          j.id === job.id ? updatedJob : j
        ))
        onJobChange()
      }
    } catch {
      alert('Error updating job. Please try again.')
    }
  }

  const handleDuplicateJob = (job) => {
    const { id: _id, ...rest } = job
    const duplicate = { ...rest, title: `${job.title} (Copy)`, active: false }
    setShowForm(true)
    setEditingJob(duplicate)
  }

  const handleFormClose = () => {
    setShowForm(false)
    setEditingJob(null)
  }

  const handleFormSubmit = async (jobData) => {
    try {
      let savedJob
      if (editingJob && editingJob.id) {
        savedJob = updateJob(editingJob.id, jobData)
        if (savedJob) {
          setJobs(jobs.map(job => 
            job.id === editingJob.id ? savedJob : job
          ))
        }
      } else {
        savedJob = addJob(jobData)
        if (savedJob) {
          setJobs([...jobs, savedJob])
        }
      }
      
      if (savedJob) {
        onJobChange()
        handleFormClose()
      }
    } catch {
      alert('Error saving job. Please try again.')
    }
  }

  const filteredJobs = jobs.filter(job => {
    const matchesFilter = filter === 'all' || (filter === 'active' ? job.active : !job.active)
    const matchesSearch = !search || job.title.toLowerCase().includes(search.toLowerCase()) || job.company.toLowerCase().includes(search.toLowerCase())
    return matchesFilter && matchesSearch
  })

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading jobs...</p>
      </div>
    )
  }

  return (
    <div className="job-manager">
      <div className="job-manager-header">
        <div className="job-manager-title">
          <h2>Job Management</h2>
          <p>Manage your job listings</p>
        </div>
        <button className="btn btn-primary" onClick={handleAddJob}>
          <i className="fas fa-plus"></i>
          Add New Job
        </button>
      </div>

      <div className="job-filters">
        <div className="job-search">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search by title or company..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="search-clear" onClick={() => setSearch('')}>
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All Jobs ({jobs.length})
          </button>
          <button 
            className={`filter-btn ${filter === 'active' ? 'active' : ''}`}
            onClick={() => setFilter('active')}
          >
            Active ({jobs.filter(job => job.active).length})
          </button>
          <button 
            className={`filter-btn ${filter === 'inactive' ? 'active' : ''}`}
            onClick={() => setFilter('inactive')}
          >
            Inactive ({jobs.filter(job => !job.active).length})
          </button>
        </div>
      </div>

      <div className="jobs-table-container">
        <table className="jobs-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Company</th>
              <th>Location</th>
              <th>Sector</th>
              <th>Type</th>
              <th>Salary</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredJobs.map(job => (
              <tr key={job.id}>
                <td>
                  <div className="job-title">
                    <strong>{job.title}</strong>
                  </div>
                </td>
                <td>{job.company}</td>
                <td>{job.location}</td>
                <td>{job.sector}</td>
                <td>{job.type}</td>
                <td>{job.salary}</td>
                <td>
                  <span className={`status-badge ${job.active ? 'active' : 'inactive'}`}>
                    {job.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="job-actions">
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleEditJob(job)}
                      title="Edit"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="btn btn-sm btn-dark"
                      onClick={() => handleDuplicateJob(job)}
                      title="Duplicate"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                    <button 
                      className={`btn btn-sm ${job.active ? 'btn-warning' : 'btn-success'}`}
                      onClick={() => handleToggleActive(job)}
                    >
                      <i className={`fas ${job.active ? 'fa-pause' : 'fa-play'}`}></i>
                    </button>
                    <button 
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteJob(job.id)}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredJobs.length === 0 && (
          <div className="no-jobs">
            <i className="fas fa-briefcase"></i>
            <h3>No jobs found</h3>
            <p>Get started by adding your first job listing.</p>
          </div>
        )}
      </div>

      {showForm && (
        <JobForm
          job={editingJob}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  )
}

export default JobManager
